import React, { useState, useEffect, useRef } from 'react';
import {
  Download, Upload, Trash2, ShieldAlert, CheckCircle2,
  RefreshCw, FileArchive, Database, Image as ImageIcon, AlertCircle, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function AdminBackup() {
  const [backups, setBackups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const restoreInputRef = useRef(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [b, l, f] = await Promise.all([
        axios.get(`${API}/admin/backup/list`),
        axios.get(`${API}/admin/logs?limit=50`),
        axios.get(`${API}/admin/files?limit=500`),
      ]);
      setBackups(b.data || []);
      setLogs(l.data || []);
      setFiles(f.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/backup/create`, {}, { responseType: 'blob' });
      // Extract filename
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `gtic-backup-${Date.now()}.zip`;

      // Trigger download
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Sauvegarde créée et téléchargée');
      fetchAll();
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBackup = async (id) => {
    try {
      await axios.delete(`${API}/admin/backup/${id}`);
      toast.success("Entrée d'historique supprimée");
      fetchAll();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleRestore = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Le fichier doit être un ZIP');
      return;
    }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/backup/restore`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRestoreResult(res.data);
      toast.success('Restauration terminée');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setRestoring(false);
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    }
  };

  const handleClearLogs = async () => {
    try {
      await axios.delete(`${API}/admin/logs`);
      toast.success('Journaux effacés');
      fetchAll();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div data-testid="admin-backup" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">
            Sauvegarde &amp; Restauration
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Téléchargez une sauvegarde complète (base de données + fichiers) ou restaurez à partir d'un ZIP.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Actions row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Create backup */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Download className="h-5 w-5 text-[#0B6AB3]" />
              Créer une sauvegarde
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Génère un fichier ZIP contenant l'export complet de la base de données et toutes les images stockées. Le téléchargement démarre automatiquement.
            </p>
            <Button
              onClick={handleCreateBackup}
              disabled={creating}
              className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white w-full sm:w-auto"
              data-testid="create-backup-btn"
            >
              {creating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Création en cours…</>
              ) : (
                <><Download className="h-4 w-4 mr-2" />Créer &amp; télécharger</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Restore */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Upload className="h-5 w-5 text-[#E50E24]" />
              Restaurer une sauvegarde
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-2">
              Importez un fichier ZIP de sauvegarde. <strong>Fusion intelligente</strong> : les éléments existants sont conservés, seuls les nouveaux sont ajoutés.
            </p>
            <input
              ref={restoreInputRef}
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              onChange={(e) => handleRestore(e.target.files?.[0])}
              data-testid="restore-file-input"
            />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  disabled={restoring}
                  className="w-full sm:w-auto border-[#E50E24] text-[#E50E24] hover:bg-red-50"
                  data-testid="restore-btn"
                >
                  {restoring ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Restauration en cours…</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" />Choisir un ZIP</>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-[#E50E24]" />
                    Confirmer la restauration
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette opération va fusionner le contenu de la sauvegarde avec la base actuelle.
                    Les éléments existants seront conservés (pas écrasés). Continuer ?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="restore-cancel">Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => restoreInputRef.current?.click()}
                    className="bg-[#E50E24] hover:bg-[#c40a1f]"
                    data-testid="restore-confirm"
                  >
                    Choisir le fichier
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>

      {/* Restore result */}
      {restoreResult && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-green-900">Restauration réussie</p>
                <div className="mt-2 text-sm text-green-800 grid sm:grid-cols-2 gap-2">
                  <div>
                    <p className="font-medium">Documents ajoutés</p>
                    <ul className="text-xs space-y-0.5 mt-1">
                      {Object.entries(restoreResult.stats?.merged || {}).map(([k, v]) => (
                        v > 0 && <li key={k}>{k}: {v}</li>
                      ))}
                      {Object.values(restoreResult.stats?.merged || {}).every(v => !v) && (
                        <li className="text-gray-600">Aucun nouveau document</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium">Fichiers</p>
                    <p className="text-xs mt-1">
                      Restaurés : {restoreResult.stats?.files_restored || 0} · Ignorés : {restoreResult.stats?.files_skipped || 0}
                    </p>
                  </div>
                </div>
              </div>
              <button onClick={() => setRestoreResult(null)} className="text-green-600 hover:text-green-800">×</button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <Database className="h-8 w-8 text-[#0B6AB3]" />
            <div>
              <p className="text-2xl font-bold text-[#0F172A]">{backups.length}</p>
              <p className="text-xs text-gray-500">Sauvegardes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <ImageIcon className="h-8 w-8 text-[#E50E24]" />
            <div>
              <p className="text-2xl font-bold text-[#0F172A]">{files.length}</p>
              <p className="text-xs text-gray-500">Fichiers stockés</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <FileArchive className="h-8 w-8 text-amber-500" />
            <div>
              <p className="text-2xl font-bold text-[#0F172A]">
                {formatBytes(files.reduce((sum, f) => sum + (f.size || 0), 0))}
              </p>
              <p className="text-xs text-gray-500">Espace utilisé</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className={`h-8 w-8 ${logs.length ? 'text-red-500' : 'text-gray-400'}`} />
            <div>
              <p className="text-2xl font-bold text-[#0F172A]">{logs.length}</p>
              <p className="text-xs text-gray-500">Erreurs récentes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Backup history */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Historique des sauvegardes</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#0B6AB3]" />
            </div>
          ) : backups.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              Aucune sauvegarde dans l'historique. Créez-en une pour démarrer.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 px-2 font-medium">Date</th>
                    <th className="py-2 px-2 font-medium">Fichier</th>
                    <th className="py-2 px-2 font-medium">Taille</th>
                    <th className="py-2 px-2 font-medium">Fichiers</th>
                    <th className="py-2 px-2 font-medium">Par</th>
                    <th className="py-2 px-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((b, idx) => (
                    <tr key={b.id} className="border-b last:border-0" data-testid={`backup-row-${idx}`}>
                      <td className="py-2 px-2 text-gray-700">{formatDate(b.created_at)}</td>
                      <td className="py-2 px-2 text-gray-700 font-mono text-xs">{b.filename}</td>
                      <td className="py-2 px-2 text-gray-700">{formatBytes(b.size)}</td>
                      <td className="py-2 px-2 text-gray-700">{b.files_count}</td>
                      <td className="py-2 px-2 text-gray-700 text-xs">{b.created_by}</td>
                      <td className="py-2 px-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBackup(b.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-backup-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error logs */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Journaux d'erreurs</CardTitle>
          {logs.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleClearLogs} data-testid="clear-logs-btn">
              <Trash2 className="h-4 w-4 mr-2" />
              Effacer
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="py-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Aucune erreur récente. Tout fonctionne normalement.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {logs.map((log) => (
                <div key={log.id} className="border-l-4 border-red-500 bg-red-50 p-3 rounded">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="destructive" className="text-xs">{log.context}</Badge>
                    <span className="text-xs text-gray-500">{formatDate(log.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-800 font-mono break-all">{log.message}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
